import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import moment from "moment";
import { makeIntComma } from "hooks/utils/util";
import { useMapStore } from "store/useMapStore";
import { ScrollArea } from "components/ui/scroll-area";
import SearchStatus from "./SearchStatus";
import SearchListItem from "./SearchListItem";
import SearchItemDetail from "./SearchItemDetail";
import { usePostMapInvoice } from "hooks/console/map/usePostMapInvoice";
import { usePostMapDriver } from "hooks/console/map/usePostMapDriver";
import { ReactComponent as Arrow } from "assets/morning/icon/arrowDownNavy.svg";

export interface DeliveryData {
    //운송장
    oKind?: string;
    o_date?: string;
    s_bl_no?: string;
    sKindName?: string;
    S_C_Contact?: string;
    S_C_Addr1?: string;
    S_C_tel?: string;
    S_C_cnt1?: number; //출발지 접수건수
    S_C_cnt2?: number; //출발지 배정건수
    S_C_cnt3?: number; //출발지 완료건수
    S_C_x?: string | null; //출발지 위치
    S_C_y?: string | null; //출발지 위치
    cKind?: string;
    pickupName?: string | null;
    pickup_reg_date?: string | null;
    pickup_tel?: string | null;
    pickup_x?: string | null; //픽업기사 위치
    pickup_y?: string | null; //픽업기사 위치
    S_B_Contact?: string;
    S_B_Addr1?: string;
    S_B_tel?: string;
    S_B_cnt1?: number; //도착지 접수건수
    S_B_cnt2?: number; //도착지 배정건수
    S_B_cnt3?: number; //도착지 완료건수
    S_B_x?: string | null; //도착지 위치
    S_B_y?: string | null; //도착지 위치
    bKind?: string;
    bpickupName?: string | null;
    bpickup_reg_date?: string | null;
    bpickup_tel?: string | null;
    bpickup_x?: string | null; //배송기사 위치
    bpickup_y?: string | null; //배송기사 위치
    //담당기사
    d_kind?: string;
    d_kind_name?: string;
    d_name?: string;
    d_e_tel?: string;
    x_location?: string | null;
    y_location?: string | null;
    reg_date?: string;
}

interface ListData {
    current_page: number;
    last_page: number;
    total_count: number;
}

interface SearchSidebarProps {
    searchValue: string;
    search: boolean;
    startDate: Date | null;
    endDate: Date | null;
    changeTab: () => void;
    selectedStatus: string[];
}

const tabs = ["운송장", "담당기사"];
const sortOptions = ["업데이트순", "등록순"];
const tabStyle = "w-full h-[50px] text-[18px] text-[#878A93] font-[700] bg-[#F7F7F8] border-b border-[#E1E2E4]";
const tabOnStyle = twMerge(
    tabStyle,
    " bg-white text-[#FF7B2E] relative after:absolute after:content-[''] after:w-full after:h-[2px] after:inset-x-0 after:-bottom-[1px] after:bg-[#FF7B2E]",
);
const sortFirstStyle = "flex [&>button]:text-[14px] [&>button]:text-[#878A93]";
const sortStyle = twMerge(
    sortFirstStyle,
    " pl-[33px] relative after:absolute after:content-[''] after:w-[1px] after:h-[16px] after:bg-[#E1E2E4] after:left-[16px] after:top-1/2 after:-translate-y-1/2",
);
const initialListData = { current_page: 1, last_page: 1, total_count: 0 };

export default function SearchSidebar({ searchValue, search, startDate, endDate, changeTab, selectedStatus }: SearchSidebarProps) {
    const { setMarkers } = useMapStore();
    const [sidebarShow, setSidebarShow] = useState(true);
    const [tabOn, setTabOn] = useState(0);
    const [sortOn, setSortOn] = useState(0);
    const [listData, setListData] = useState<ListData>(initialListData);
    const [list, setList] = useState<DeliveryData[]>([]);
    const [loading, setLoading] = useState(false);
    const [bottomBox, setBottomBox] = useState(false);
    const [detailOn, setDetailOn] = useState<{ idx: number | null; blNo: string | null }>({ idx: null, blNo: null });
    const scrollAreaRef = useRef<HTMLDivElement | null>(null);
    const bottomRef = useRef<HTMLLIElement | null>(null);
    const isFirstRender = useRef(true);
    const sidebarClass = sidebarShow ? "opacity-1" : detailOn.idx !== null ? "-ml-[692px] opacity-0" : "-ml-[320px] opacity-0";

    const { mutateAsync: postInvoice } = usePostMapInvoice(() => {});
    const { mutateAsync: postDriver } = usePostMapDriver(() => {});

    //운송장 목록 조회 || 담당기사 목록 조회
    const getList = async (page: number, signal?: AbortSignal) => {
        setLoading(true);
        try {
            const baseBody = {
                getLimit: "100",
                getPage: page.toString(),
            };
            let extraBody = {};
            //운송장 목록
            if (tabOn === 0) {
                extraBody = {
                    ...baseBody,
                    company: searchValue,
                    sDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
                    eDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
                    oKind: selectedStatus,
                    getOrderby: sortOn === 0 ? "o_date" : sortOn === 1 ? "s_bl_no" : "",
                    getAscDesc: "asc",
                };
            }
            //담당기사 목록
            if (tabOn === 1) {
                extraBody = {
                    ...baseBody,
                    d_name: searchValue,
                };
            }

            const body = { ...baseBody, ...extraBody };
            const action = tabOn === 0 ? postInvoice(body) : tabOn === 1 && postDriver(body);
            const res = await action;

            if (signal && signal.aborted) {
                setLoading(false);
                return; // 요청이 취소된 경우 처리 중단
            }

            if (res.statusCode === 200) {
                const { current_page, last_page, total_count, bl_list, driver_list } = res.data;

                // 탭에 따른 리스트 데이터 선택
                // const updatedList = tabOn === 0 ? bl_list : driver_list;

                // 리스트 업데이트
                // setList(prevList => [...prevList, ...updatedList]);

                if (tabOn === 0) {
                    setList(prev => {
                        const existingIds = new Set(prev.map(list => list.s_bl_no));
                        const newList = bl_list.filter((list: DeliveryData) => !existingIds.has(list.s_bl_no)); // 중복되지 않는 항목 필터링
                        return [...prev, ...newList]; // 기존 항목과 새로운 항목 합치기
                    });
                } else {
                    setList(prevList => [...prevList, ...driver_list]);
                }

                // 공통 데이터 설정
                setListData({
                    current_page,
                    last_page,
                    total_count,
                });
            }
        } catch (error) {
            console.error("Failed to fetch list:", error);
        } finally {
            setLoading(false);
        }
    };

    // 목록 초기화 함수
    const resetListData = () => {
        setListData(initialListData);
        setList([]);
        setDetailOn({ idx: null, blNo: null });
    };

    //탭 변경시 목록조회 && 운송장 상세정보 닫기 && 정렬탭 초기화
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false; // 초기 렌더링 이후에만 실행
            return;
        }

        const controller = new AbortController();
        const signal = controller.signal;

        resetListData();
        getList(1, signal);
        changeTab();
        setSortOn(0);

        return () => {
            controller.abort(); // 이전 요청 취소
        };
    }, [tabOn]);

    //정렬탭 변경시 목록조회 && 운송장 상세정보 닫기
    useEffect(() => {
        if (isFirstRender.current) return; // 초기 렌더링 방지
        const controller = new AbortController();
        const signal = controller.signal;
        if (tabOn === 0) {
            resetListData();
            getList(1, signal);
        }
        return () => {
            controller.abort(); // 이전 요청 취소
        };
    }, [sortOn]);

    //헤더에서 검색버튼 클릭시 목록 검색
    useEffect(() => {
        if (search) {
            resetListData();
            getList(1);
        }
    }, [search]);

    //목록 변경시 지도마커값 변경
    useEffect(() => {
        let updatedMarkers = [];

        //운송장 목록일때
        if (tabOn === 0) {
            updatedMarkers = list.flatMap((item, index) => {
                const markerArray: any = [];
                const locations = [
                    {
                        x: item.S_C_x,
                        y: item.S_C_y,
                        isDelivery: true,
                        isOrigin: true,
                        isPickup: false,
                        index: `${index}_0`,
                    }, // 출발지
                    {
                        x: item.pickup_x,
                        y: item.pickup_y,
                        isDelivery: false,
                        isCourierAssigned: true,
                        isPickup: true,
                        index: `${index}_1`,
                    }, // 픽업기사
                    {
                        x: item.S_B_x,
                        y: item.S_B_y,
                        isDelivery: true,
                        isOrigin: false,
                        isPickup: false,
                        index: `${index}_2`,
                    }, // 도착지
                    {
                        x: item.bpickup_x,
                        y: item.bpickup_y,
                        isDelivery: false,
                        isCourierAssigned: true,
                        isPickup: false,
                        index: `${index}_3`,
                    }, // 배송기사
                ];

                locations.forEach(loc => {
                    if (loc.x && loc.y) {
                        markerArray.push({
                            lat: Number(loc.y),
                            lng: Number(loc.x),
                            isOpen: false,
                            isDelivery: loc.isDelivery,
                            isOrigin: loc.isOrigin,
                            isCourierAssigned: loc.isCourierAssigned,
                            isPickup: loc.isPickup,
                            markerData: item,
                            index: loc.index,
                        });
                    }
                });

                return markerArray;
            });
        }
        //담당기사 목록일때
        if (tabOn === 1) {
            updatedMarkers = list
                .map((item, index) => ({ ...item, index })) // 원래 index를 저장
                .filter(item => item.x_location !== null && item.y_location !== null)
                .map(item => ({
                    lat: Number(item.y_location),
                    lng: Number(item.x_location),
                    isOpen: false,
                    isDelivery: false,
                    isPickup: item.d_kind === "3" ? true : false,
                    markerData: item,
                    index: item.index,
                }));
        }

        setMarkers(updatedMarkers);
    }, [list]);

    //목록 맨밑으로 스크롤시 다음페이지 목록 요청
    useEffect(() => {
        // Intersection Observer 설정
        const observer = new IntersectionObserver(
            entries => {
                const [entry] = entries;
                if (entry.isIntersecting && !loading && listData.current_page < listData.last_page) {
                    // 다음 페이지 데이터 요청
                    const page = listData.current_page + 1;
                    getList(page);
                }
            },
            { root: scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]"), threshold: 1.0 },
        );

        if (bottomRef.current && bottomBox) observer.observe(bottomRef.current);

        return () => {
            if (bottomRef.current) observer.unobserve(bottomRef.current);
        };
    }, [loading, listData, bottomBox]);

    //목록 맨밑영역 노출 결졍
    useEffect(() => {
        if (!loading && listData.current_page < listData.last_page) {
            setBottomBox(true);
        } else {
            setBottomBox(false);
        }
    }, [listData, loading]);

    return (
        <div className="flex relative">
            <div className={`transition-all ${sidebarClass}`}>
                <div className="flex-1 w-[320px] flex flex-col">
                    <ul className="flex">
                        {tabs.map((tab, i) => {
                            return (
                                <li key={`tab_${i}`} className="w-1/2">
                                    <button type="button" className={tabOn === i ? tabOnStyle : tabStyle} onClick={() => setTabOn(i)}>
                                        {tab}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                    <ScrollArea
                        className="w-full h-full border-r border-[#E1E2E4] bg-white [&>div>div]:h-full overflow-hidden"
                        ref={scrollAreaRef}
                    >
                        <div className="flex items-center justify-between px-[20px] h-[50px] border-b border-[#E1E2E4]">
                            <p className="text-[15px] font-[500]">
                                총<span className="p-[0_2px_0_5px] text-[#FF7B2E]">{makeIntComma(listData?.total_count || 0)}</span>건
                            </p>
                            {tabOn === 0 && (
                                <ul className="flex">
                                    {sortOptions.map((sort, i) => (
                                        <li key={`sort_${i}`} className={i === 0 ? sortFirstStyle : sortStyle}>
                                            <button
                                                type="button"
                                                className={sortOn === i ? "!text-[#1B1C1E]" : ""}
                                                onClick={() => setSortOn(i)}
                                            >
                                                {sort}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {list.length > 0 ? (
                            <ul className="overflow-auto max-h-[calc(100dvh-224px)]">
                                {list.map((cont, i) => {
                                    return (
                                        <li key={`list_${i}`} className={detailOn.idx === i ? "bg-[#F7F7F8]" : ""}>
                                            <SearchListItem
                                                data={cont}
                                                type={tabOn}
                                                setDetailOn={(idx, blNo) => setDetailOn({ idx, blNo })}
                                                index={i}
                                            />
                                        </li>
                                    );
                                })}
                                {bottomBox && <li ref={bottomRef} className="h-[1px]" />}
                            </ul>
                        ) : (
                            <div className="min-h-[calc(100dvh-224px)] flex items-center justify-center">
                                <SearchStatus type={loading ? "loading" : "empty"} />
                            </div>
                        )}
                    </ScrollArea>
                </div>
                {tabOn === 0 && detailOn.blNo && (
                    <SearchItemDetail index={detailOn.blNo} handleClose={() => setDetailOn({ idx: null, blNo: null })} />
                )}
            </div>
            <button
                type="button"
                className="z-[1111] absolute top-0 left-full px-1 h-[50px] bg-white rounded-[0_5px_5px_0] border-t border-b border-r"
                onClick={() => setSidebarShow(!sidebarShow)}
            >
                <div>
                    <Arrow className={sidebarShow ? "rotate-90" : "rotate-[-90deg]"} />
                </div>
            </button>
        </div>
    );
}
